import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CtaSelectService } from '@campaign-form/form-fields/creators-and-ctas/cta-select.service';
import { NumberLocalePipe } from '@core/pipes/number-locale.pipe';
import { NumberSuffixPipe } from '@core/pipes/number-suffix.pipe';
import { PercentagePipe } from '@core/pipes/percentage.pipe';
import { EventTrackingService } from '@core/services/event-tracking.service';
import { EPlatformFeature } from '@enums/campaign.enum';
import { EEventType } from '@enums/event-tracking.enum';
import { IMatchedKpiAnalyticsApi, TKpiAnalyticsKey } from '@interfaces/campaign-analytics-api.interface';
import { TExpectedKpi } from '@interfaces/campaign-analytics.interface';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ICreatorChannelAnalyticsResponse } from '@pages/creators/creators.interface';
import { IconComponent } from '@shared/components/icon/icon.component';
import { SocialBadgeComponent } from '@shared/components/social-badge/social-badge.component';
import { TextClampTooltipDirective } from '@shared/directives/text-clamp-tooltip.directive';
import { TrackInfoHoverDirective } from '@shared/directives/track-info-hover.directive';
import { toEventFrontendTab } from '@utilities/event-payload-mapping.util';
import { SocialUtilitiesService } from '@utilities/social-utilities.service';
import { camelCase } from 'lodash';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { map, startWith } from 'rxjs';
import { CampaignDetailsDeliverablesFilterSelectComponent } from '../campaign-details-deliverables/campaign-details-deliverables-filter-select/campaign-details-deliverables-filter-select.component';
import { ICampaignDetailsDeliverablesFilterOption } from '../campaign-details-deliverables/campaign-details-deliverables.interface';
import { ECampaignDetailsTab, ICampaignAnalyticsFilter } from '../campaign-details.interface';
import {
  IKpiAggregateValues,
  IKpiCard,
  IKpiCardPlatformRow,
  IPlatformFeatureKpis,
  TKpiAggregateColumnKey,
} from './campaign-details-kpis.interface';
import { CampaignDetailsKpisService } from './campaign-details-kpis.service';
import {
  CampaignDetailsKpisStore,
  ICampaignDetailsKpisStoreParams,
  IFetchPlatformFeatureKpisParams,
} from './campaign-details-kpis.store';

const PERFORMANCE_TAB_SELECTED_KPIS_KEY = 'shikenso_performance_tab_selected_kpis';
const PERFORMANCE_TAB_PINNED_KPIS_KEY = 'shikenso_performance_tab_pinned_kpis';

const EXPECTED_KPI_TO_ANALYTICS_KEY_MAP: Record<TExpectedKpi, TKpiAnalyticsKey> = {
  averageViewers: 'avgViewers',
  comments: 'comments',
  engagementRate: 'engagementRate',
  engagementRateViews: 'engagementRateViews',
  engagements: 'engagements',
  impressions: 'impressions',
  likes: 'likes',
  mediaValue: 'mediaValue',
  peakViewers: 'peakViewers',
  predictedImpressions: 'predictedImpressions',
  quotes: 'quotes',
  reach: 'reach',
  retweets: 'retweets',
  shares: 'shares',
  sponsoredAverageViewers: 'sponsoredAvgViewers',
  sponsoredPeakViewers: 'sponsoredPeakViewers',
  sponsoredTotalWatchedHours: 'sponsoredTotalWatchedHours',
  totalWatchedHours: 'totalWatchedHours',
  views: 'views',
};

const KPI_COLUMN_MAP: Record<TKpiAnalyticsKey, TKpiAggregateColumnKey[]> = {
  avgViewers: ['avg', 'base'],
  comments: ['total', 'avg', 'base'],
  engagementRate: ['avg', 'base'],
  engagementRateViews: ['avg', 'base'],
  engagements: ['total', 'avg', 'base'],
  impressions: ['total', 'avg', 'base'],
  likes: ['total', 'avg', 'base'],
  mediaValue: ['total', 'avg', 'base'],
  peakViewers: ['max', 'avg', 'base'],
  predictedImpressions: ['total', 'avg', 'base'],
  quotes: ['total', 'avg', 'base'],
  reach: ['total', 'avg', 'base'],
  retweets: ['total', 'avg', 'base'],
  shares: ['total', 'avg', 'base'],
  sponsoredAvgViewers: ['avg', 'base'],
  sponsoredPeakViewers: ['max', 'avg', 'base'],
  sponsoredTotalWatchedHours: ['total', 'avg', 'base'],
  totalWatchedHours: ['total', 'avg', 'base'],
  views: ['total', 'avg', 'base'],
};

const KPI_ICON_MAP: Partial<Record<TKpiAnalyticsKey, string>> = {
  avgViewers: 'icons/view.svg',
  comments: 'icons/chat.svg',
  engagementRate: 'icons/rate.svg',
  engagementRateViews: 'icons/rate.svg',
  engagements: 'icons/engagement.svg',
  impressions: 'icons/impression.svg',
  likes: 'icons/thumbs-up.svg',
  mediaValue: 'icons/money.svg',
  peakViewers: 'icons/view.svg',
  predictedImpressions: 'icons/predict.svg',
  quotes: 'icons/quotes.svg',
  reach: 'icons/reach.svg',
  retweets: 'icons/retweet.svg',
  shares: 'icons/share.svg',
  sponsoredAvgViewers: 'icons/view.svg',
  sponsoredPeakViewers: 'icons/view.svg',
  sponsoredTotalWatchedHours: 'icons/clock.svg',
  totalWatchedHours: 'icons/clock.svg',
  views: 'icons/view.svg',
};

@Component({
  animations: [
    trigger('expandCollapse', [
      state('collapsed', style({ height: '0px', opacity: 0, overflow: 'hidden' })),
      state('expanded', style({ height: '*', opacity: 1, overflow: 'hidden' })),
      transition('collapsed => expanded', [animate('200ms ease-out')]),
      transition('expanded => collapsed', [animate('200ms ease-in')]),
    ]),
  ],
  imports: [
    CampaignDetailsDeliverablesFilterSelectComponent,
    IconComponent,
    MatButtonModule,
    MatTooltipModule,
    NgxSkeletonLoaderModule,
    NumberLocalePipe,
    NumberSuffixPipe,
    PercentagePipe,
    SocialBadgeComponent,
    TextClampTooltipDirective,
    TrackInfoHoverDirective,
    TranslateModule,
  ],
  providers: [CampaignDetailsKpisService, CampaignDetailsKpisStore],
  selector: 'app-campaign-details-kpis',
  templateUrl: './campaign-details-kpis.component.html',
})
export class CampaignDetailsKpisComponent {
  public readonly matchedKpis = input.required<IMatchedKpiAnalyticsApi[]>();
  public readonly loading = input<boolean>(true);
  public readonly campaignId = input.required<string>();
  public readonly exportMode = input<boolean>(false);
  public readonly campaignExportId = input<string | null>(null);
  public readonly exportCampaignId = input<string | null>(null);
  public readonly campaignPlatformFeatures = input.required<EPlatformFeature[]>();
  public readonly campaignAnalyticsFilter = input.required<ICampaignAnalyticsFilter>();

  protected readonly currentTab = toEventFrontendTab(ECampaignDetailsTab.performance);

  public readonly store = inject(CampaignDetailsKpisStore);
  private readonly kpisService = inject(CampaignDetailsKpisService);
  private readonly translate = inject(TranslateService);
  private readonly eventTrackingService = inject(EventTrackingService);

  public readonly languageChange = toSignal(
    this.translate.onLangChange.pipe(
      map((event) => event.lang),
      startWith(this.translate.currentLang),
    ),
    { initialValue: this.translate.currentLang },
  );

  public readonly collapsedCards = signal<Set<string>>(new Set<string>());
  public readonly pinnedKpis = signal<string[]>(this.loadPinnedFromStorage());
  public readonly pinnedKpisSet = computed<Set<string>>(() => new Set(this.pinnedKpis()));
  public readonly selectedKpiKeys = signal<Set<string>>(this.loadSelectedKpisFromStorage());

  public readonly activePlatformFeatures = computed<EPlatformFeature[]>(() => {
    const selectedPfs = this.campaignAnalyticsFilter().platformFeatures;

    return selectedPfs.size > 0 ? Array.from(selectedPfs) : this.campaignPlatformFeatures();
  });

  public readonly platformFeatureOrder = new Map(
    Object.values(CtaSelectService.allCtaOptions).map((ctaOption, index) => [ctaOption.platformFeature, index]),
  );

  public readonly platformFeatureKpis = computed<IPlatformFeatureKpis[]>(() => {
    const matchedKpis: IMatchedKpiAnalyticsApi[] = this.matchedKpis();
    const selectedPlatformFeatures: Set<EPlatformFeature> = this.campaignAnalyticsFilter().platformFeatures;

    return this.kpisService.buildPlatformFeatureKpis(matchedKpis, selectedPlatformFeatures);
  });

  public readonly allAvailableKpiKeys = computed<TKpiAnalyticsKey[]>(() => {
    const kpisMap: Map<EPlatformFeature, TExpectedKpi[]> = this.store.platformFeatureKpisMap$();
    const allExpectedKpis: Set<TExpectedKpi> = new Set<TExpectedKpi>();

    for (const kpis of kpisMap.values()) {
      kpis.forEach((k) => allExpectedKpis.add(k));
    }

    const analyticsKeys: Set<TKpiAnalyticsKey> = new Set<TKpiAnalyticsKey>();

    for (const expectedKpi of allExpectedKpis) {
      const analyticsKey: TKpiAnalyticsKey = EXPECTED_KPI_TO_ANALYTICS_KEY_MAP[expectedKpi];

      analyticsKeys.add(analyticsKey);
    }

    return Array.from(analyticsKeys);
  });

  public readonly kpiFilterOptions = computed<ICampaignDetailsDeliverablesFilterOption<string>[]>(() => {
    this.languageChange();

    return this.sortKpiKeysByTranslation(this.allAvailableKpiKeys()).map((key) => ({
      label: this.translate.instant(`pages.campaignDetails.kpis.${key}`) as string,
      value: key,
    }));
  });

  public readonly kpiFilterEmptyLabel = computed<string>(() => {
    this.languageChange();

    return this.translate.instant('pages.campaignDetails.kpis.filters.allKpis', {
      number: this.allAvailableKpiKeys().length,
    }) as string;
  });

  public readonly kpiCards = computed<IKpiCard[]>(() => {
    const kpisMap: Map<EPlatformFeature, TExpectedKpi[]> = this.store.platformFeatureKpisMap$();
    const matchedKpisData: IPlatformFeatureKpis[] = this.platformFeatureKpis();
    const baselineData: ICreatorChannelAnalyticsResponse[] | null = this.store.baselineData$();
    const activePfs: EPlatformFeature[] = this.activePlatformFeatures();
    const selectedCreatorIds: Set<string> = this.campaignAnalyticsFilter().creatorIds;

    const matchedKpisByPlatform = new Map<EPlatformFeature, Partial<Record<TKpiAnalyticsKey, IKpiAggregateValues>>>(
      matchedKpisData.map((mk) => [mk.platformFeature, mk.kpis]),
    );

    const keysToShow: TKpiAnalyticsKey[] = this.getKeysToShow();

    const sortedPfs: EPlatformFeature[] = [...activePfs].sort((a, b) => {
      const aOrder: number = this.platformFeatureOrder.get(a) ?? Number.MAX_SAFE_INTEGER;
      const bOrder: number = this.platformFeatureOrder.get(b) ?? Number.MAX_SAFE_INTEGER;

      return aOrder - bOrder;
    });

    const baselineLookup: Map<string, number | null> = this.buildBaselineLookup(baselineData, selectedCreatorIds);

    return keysToShow.map((analyticsKey) => {
      const columns: TKpiAggregateColumnKey[] = KPI_COLUMN_MAP[analyticsKey];

      return {
        analyticsKey,
        columns,
        gridTemplate: this.getTableGridTemplate(columns.length),
        icon: KPI_ICON_MAP[analyticsKey] ?? 'icons/analytics.svg',
        isPercentage: this.isPercentageKpi(analyticsKey),
        platformRows: this.buildPlatformRows(analyticsKey, kpisMap, sortedPfs, matchedKpisByPlatform, baselineLookup),
      };
    });
  });

  public readonly orderedKpiCards = computed<IKpiCard[]>(() => {
    this.languageChange();

    const cards: IKpiCard[] = this.kpiCards();
    const pinned: string[] = this.pinnedKpis();
    const pinnedSet: Set<string> = this.pinnedKpisSet();
    const cardMap: Map<TKpiAnalyticsKey, IKpiCard> = new Map(cards.map((c) => [c.analyticsKey, c]));
    const pinnedCards: IKpiCard[] = [];
    const unpinnedCards: IKpiCard[] = [];

    for (const key of pinned) {
      const card: IKpiCard | undefined = cardMap.get(key as TKpiAnalyticsKey);

      if (card) {
        pinnedCards.push(card);
      }
    }

    for (const card of cards) {
      if (!pinnedSet.has(card.analyticsKey)) {
        unpinnedCards.push(card);
      }
    }

    const sortedUnpinnedKeys: TKpiAnalyticsKey[] = this.sortKpiKeysByTranslation(
      unpinnedCards.map((c) => c.analyticsKey),
    );

    return [...pinnedCards, ...sortedUnpinnedKeys.map((k) => cardMap.get(k)!)];
  });

  public readonly isOverallLoading = computed<boolean>(
    () => this.loading() || this.store.platformFeatureKpisLoading$(),
  );

  constructor() {
    effect(() => {
      const platformFeatures = this.activePlatformFeatures();
      const exportMode = this.exportMode();
      const campaignExportId = this.campaignExportId();
      const params: IFetchPlatformFeatureKpisParams = {
        campaignExportId,
        exportMode,
        platformFeatures,
      };

      this.store.fetchPlatformFeatureKpisForFeatures(params);
    });

    effect(() => {
      const campaignId = this.campaignId();
      const exportMode = this.exportMode();
      const campaignExportId = this.campaignExportId();

      if (campaignId || campaignExportId) {
        const params: ICampaignDetailsKpisStoreParams = {
          campaignExportId,
          campaignId,
          exportMode,
        };
        this.store.fetchBaselineData(params);
      }
    });
  }

  public isCardExpanded(analyticsKey: string): boolean {
    return !this.collapsedCards().has(analyticsKey);
  }

  public toggleCard(analyticsKey: string): void {
    const collapsed = new Set(this.collapsedCards());

    if (collapsed.has(analyticsKey)) {
      collapsed.delete(analyticsKey);
    } else {
      collapsed.add(analyticsKey);
    }

    this.collapsedCards.set(collapsed);
  }

  public isPinned(analyticsKey: string): boolean {
    return this.pinnedKpisSet().has(analyticsKey);
  }

  public togglePin(analyticsKey: string, event: Event): void {
    event.stopPropagation();
    const pinned = [...this.pinnedKpis()];
    const idx = pinned.indexOf(analyticsKey);
    const wasPinned = idx >= 0;

    if (wasPinned) {
      pinned.splice(idx, 1);
    } else {
      pinned.push(analyticsKey);
    }

    this.pinnedKpis.set(pinned);
    this.savePinnedToStorage(pinned);

    this.eventTrackingService.track(
      wasPinned ? EEventType.campaignAnalyticsKpiUnpinned : EEventType.campaignAnalyticsKpiPinned,
      {
        campaignId: this.campaignId(),
        pinnedKpi: analyticsKey,
      },
    );
  }

  public onKpiSelectionChanged(selected: Set<string>): void {
    this.selectedKpiKeys.set(selected);
    this.saveSelectedKpisToStorage(selected);
  }

  public getColumnTooltipTranslationKey(column: TKpiAggregateColumnKey, analyticsKey?: TKpiAnalyticsKey): string {
    if (
      [
        'avgViewers',
        'peakViewers',
        'totalWatchedHours',
        'sponsoredAvgViewers',
        'sponsoredPeakViewers',
        'sponsoredTotalWatchedHours',
      ].includes(analyticsKey ?? '') &&
      column === 'base'
    ) {
      return `pages.campaignDetails.kpis.table.${column}SponsoredTooltip`;
    }

    return `pages.campaignDetails.kpis.table.${column}Tooltip`;
  }

  public getColumnTranslationKey(column: TKpiAggregateColumnKey): string {
    return `pages.campaignDetails.kpis.table.${column}`;
  }

  public getKpiTranslationKey(analyticsKey: TKpiAnalyticsKey): string {
    return `pages.campaignDetails.kpis.${analyticsKey}`;
  }

  public getTableGridTemplate(columnCount: number): string {
    return `minmax(0, 1.6fr) repeat(${columnCount}, minmax(0, 1fr))`;
  }

  public getRowColumnValue(row: IKpiCardPlatformRow, column: TKpiAggregateColumnKey): number | null {
    switch (column) {
      case 'avg':
        return row.avgValue;
      case 'base':
        return row.baseValue;
      case 'max':
        return row.maxValue;
      case 'total':
        return row.totalValue;
    }

    return null;
  }

  public isPercentageKpi(analyticsKey: TKpiAnalyticsKey): boolean {
    return analyticsKey === 'engagementRate' || analyticsKey === 'engagementRateViews';
  }

  public getPlatformColor(platformFeature: EPlatformFeature): string {
    return SocialUtilitiesService.platformColorMap[SocialUtilitiesService.getPlatform(platformFeature)];
  }

  private getKeysToShow(): TKpiAnalyticsKey[] {
    const selected: Set<string> = this.selectedKpiKeys();
    const available: TKpiAnalyticsKey[] = this.allAvailableKpiKeys();

    return selected.size === 0 ? available : available.filter((k) => selected.has(k));
  }

  private buildPlatformRows(
    analyticsKey: TKpiAnalyticsKey,
    kpisMap: Map<EPlatformFeature, TExpectedKpi[]>,
    sortedPfs: EPlatformFeature[],
    matchedKpisByPlatform: Map<EPlatformFeature, Partial<Record<TKpiAnalyticsKey, IKpiAggregateValues>>>,
    baselineLookup: Map<string, number | null>,
  ): IKpiCardPlatformRow[] {
    return sortedPfs.reduce<IKpiCardPlatformRow[]>((rows, pf) => {
      const pfKpis: TExpectedKpi[] | undefined = kpisMap.get(pf);

      if (!pfKpis) {
        return rows;
      }

      const supportsKpi: boolean = pfKpis.some(
        (expectedKpi) => EXPECTED_KPI_TO_ANALYTICS_KEY_MAP[expectedKpi] === analyticsKey,
      );

      if (!supportsKpi) {
        return rows;
      }

      const matchedKpis: Partial<Record<TKpiAnalyticsKey, IKpiAggregateValues>> | undefined =
        matchedKpisByPlatform.get(pf);
      const aggregateValues: IKpiAggregateValues | undefined = matchedKpis?.[analyticsKey];
      const {
        avgValue = null,
        maxValue = null,
        totalValue = null,
      }: Partial<IKpiAggregateValues> = aggregateValues ?? {};

      const baseValue: number | null =
        baselineLookup.get(`${pf}:${analyticsKey}`) ??
        baselineLookup.get(`${pf}:${camelCase(analyticsKey.replace('sponsored', ''))}`) ??
        null;

      rows.push({
        avgValue,
        baseValue,
        maxValue,
        platformColor: this.getPlatformColor(pf),
        platformFeature: pf,
        totalValue,
      });

      return rows;
    }, []);
  }

  private buildBaselineLookup(
    baselineData: ICreatorChannelAnalyticsResponse[] | null,
    selectedCreatorIds: Set<string>,
  ): Map<string, number | null> {
    const lookup = new Map<string, number | null>();

    if (!baselineData) {
      return lookup;
    }

    const filteredCreators: ICreatorChannelAnalyticsResponse[] =
      selectedCreatorIds.size > 0 ? baselineData.filter((c) => selectedCreatorIds.has(c.id)) : baselineData;

    const valuesMap = new Map<string, number[]>();

    for (const creator of filteredCreators) {
      for (const channel of creator.channelAnalytics) {
        for (const kpiEntry of channel.avgKpis) {
          const pf: EPlatformFeature = camelCase(kpiEntry.platformFeature) as EPlatformFeature;
          const { avgKpis } = kpiEntry;

          for (const [key, val] of Object.entries(avgKpis) as [TKpiAnalyticsKey, number | null | undefined][]) {
            if (typeof val === 'number') {
              const lookupKey = `${pf}:${key}`;
              const arr: number[] = valuesMap.get(lookupKey) ?? [];
              arr.push(val);
              valuesMap.set(lookupKey, arr);
            }
          }
        }
      }
    }

    for (const [key, values] of valuesMap) {
      lookup.set(key, values.reduce((sum, v) => sum + v, 0) / values.length);
    }

    return lookup;
  }

  private sortKpiKeysByTranslation(keys: TKpiAnalyticsKey[]): TKpiAnalyticsKey[] {
    return [...keys].sort((a, b) => {
      const aLabel: string = this.translate.instant(this.getKpiTranslationKey(a)) as string;
      const bLabel: string = this.translate.instant(this.getKpiTranslationKey(b)) as string;

      return aLabel.localeCompare(bLabel);
    });
  }

  private loadPinnedFromStorage(): string[] {
    try {
      const raw = localStorage.getItem(PERFORMANCE_TAB_PINNED_KPIS_KEY);

      return raw ? (JSON.parse(raw) as string[]) : [];
    } catch {
      return [];
    }
  }

  private savePinnedToStorage(pinned: string[]): void {
    try {
      localStorage.setItem(PERFORMANCE_TAB_PINNED_KPIS_KEY, JSON.stringify(pinned));
    } catch {
      // ignore storage errors
    }
  }

  private loadSelectedKpisFromStorage(): Set<string> {
    try {
      const raw = localStorage.getItem(PERFORMANCE_TAB_SELECTED_KPIS_KEY);

      return raw ? new Set<string>(JSON.parse(raw) as string[]) : new Set<string>();
    } catch {
      return new Set<string>();
    }
  }

  private saveSelectedKpisToStorage(selected: Set<string>): void {
    try {
      localStorage.setItem(PERFORMANCE_TAB_SELECTED_KPIS_KEY, JSON.stringify(Array.from(selected)));
    } catch {
      // ignore storage errors
    }
  }
}
